import { useState } from 'react';
import Button from 'components/Buttons/Button';

interface SubscriptionItem {
  uuid: string;
  itemType: string;
  quantity: number;
  itemName: string;
  limit: number;
  limitPeriod: string;
}

interface SubscriptionTier {
  uuid: string;
  title: string;
  description: string;
  price: number;
  cycle: string;
  rules: string[];
  maxSubscribers: number;
  subscribersCount: number;
  currentSubscribers: number;
  isActive: boolean;
  items: SubscriptionItem[];
}

interface ISubscriptionTierCardProps {
  subscription: SubscriptionTier;
  onEdit?: (subscription: SubscriptionTier) => void;
  onToggle?: (subscription: SubscriptionTier, newState: boolean) => void;
  isExample?: boolean;
}

const formatCycle = (cycle: string): string => {
  const cycleMap: { [key: string]: string } = {
    DAILY: 'Day',
    WEEKLY: 'Week',
    MONTHLY: 'Month',
    '3_MONTH': '3 Months',
    '6_MONTH': '6 Months',
    YEARLY: 'Year',
  };
  return cycleMap[cycle] || cycle;
};

const formatLimitPeriod = (period: string): string => {
  const periodMap: { [key: string]: string } = {
    PER_HOUR: 'Hour',
    PER_DAY: 'Day',
    PER_WEEK: 'Week',
    PER_MONTH: 'Month',
  };
  return periodMap[period] || period;
};

const SubscriptionTierCard: React.FC<ISubscriptionTierCardProps> = ({
  subscription,
  onEdit,
  onToggle,
  isExample = false,
}) => {
  const {
    title,
    description,
    price,
    cycle,
    rules,
    currentSubscribers,
    maxSubscribers,
    items,
    isActive,
  } = subscription;

  const [isToggled, setIsToggled] = useState(isActive);

  const handleToggleChange = () => {
    const newState = !isToggled;
    setIsToggled(newState);
    if (onToggle) {
      onToggle(subscription, newState);
    }
  };

  return (
    <div
      className={`w-full max-w-[340px] md:w-[340px] min-h-[473px] rounded-2xl overflow-hidden flex flex-col relative hover:shadow-md transition-shadow ${
        isToggled ? 'bg-[#E8E8FF]' : 'border-2 border-duskLavender'
      }`}
    >
      {/* Top Header - Subscription Count */}
      <div
        className={`px-3 md:px-4 py-2 md:py-3 ${!isExample ? (isToggled ? 'bg-duskLavender' : 'border-b-2 border-duskLavender') : ''}`}
      >
        {!isExample && (
          <h4
            className={`font-poppins font-semibold text-xs leading-none capitalize ${
              isToggled ? 'text-white' : 'text-duskLavender'
            }`}
          >
            {currentSubscribers} Subscriptions
          </h4>
        )}
      </div>

      {/* Card Content */}
      <div className="p-3 md:p-4 flex flex-col gap-3 md:gap-4 flex-1">
        {/* Inner Card with White Background */}
        <div className="w-full border border-duskLavender rounded-xl bg-white flex flex-col gap-2 p-3 md:p-4">
          {/* Title and Description Container */}
          <div className="w-full flex flex-col gap-3 md:gap-4">
            {/* Title */}
            <h3 className="font-poppins font-semibold text-base md:text-lg leading-none capitalize text-[#4C4BC9]">
              {title}
            </h3>

            {/* Description */}
            <p className="line-clamp-3 font-poppins font-normal text-xs leading-tight text-[#757575]">
              {description}
            </p>
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-1">
            <span className="font-poppins font-normal text-2xl md:text-[28px] leading-none text-[#4C4BC9]">
              ${price.toFixed(2)}
            </span>
            <span className="font-poppins font-normal text-sm leading-none text-[#4C4BC9]">
              / {formatCycle(cycle)}
            </span>
          </div>

          {/* Items */}
          {items && items.length > 0 && (
            <div className="bg-lightPinkBg text-royalPink font-poppins font-medium text-xs leading-relaxed capitalize p-2 px-3 md:px-4 rounded-lg w-fit flex flex-col gap-1">
              {items.map((item, index) => (
                <div key={item.uuid || index}>
                  {item.quantity} X {item.itemName} Limit {item.limit} Per{' '}
                  {formatLimitPeriod(item.limitPeriod)}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Store Rules - For all cards inside the card content */}
        {rules && rules.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4 className="font-poppins font-medium text-sm leading-none capitalize text-[#757575]">
              Store Rules
            </h4>
            <ul className="list-disc list-inside flex flex-col gap-2">
              {rules.map((rule, index) => (
                <li
                  key={index}
                  className="font-poppins font-normal text-xs md:text-sm leading-relaxed capitalize text-black"
                >
                  {rule}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Bottom Section - Outside padded content, at the very end */}
      <div
        className={`flex items-center px-3 md:px-4 pb-3 md:pb-4 pt-2 md:pt-3 ${isExample ? 'justify-start' : 'justify-between'}`}
      >
        {isExample ? (
          <Button
            onClick={() => onEdit && onEdit(subscription)}
            label="Use this example"
            variant="secondary"
            customClass="!w-[153px] !h-[34px] !px-5 !py-2 !text-xs !mb-0 !border !border-duskLavender !text-[#0102CE] hover:!bg-primary hover:!text-white !rounded-full"
          />
        ) : (
          <>
            <Button
              onClick={() => onEdit && onEdit(subscription)}
              label="Edit"
              variant="secondary"
              disabled={currentSubscribers !== 0}
              customClass={`!w-[80px] !h-[34px] !px-5 !py-2 !text-xs !mb-0 !rounded-full ${
                currentSubscribers !== 0
                  ? '!border !border-gray-300 !text-gray-400 !cursor-not-allowed'
                  : '!border !border-duskLavender !text-[#0102CE] hover:!bg-primary hover:!text-white'
              }`}
            />

            {/* Toggle Switch with Status Text */}
            <div className="flex items-center gap-1.5 md:gap-2">
              <div className="flex items-center gap-1">
                <div
                  className={`w-2 h-2 rounded-full ${isToggled ? 'bg-[#049523]' : 'bg-[#FB4C2F]'}`}
                />
                <span
                  className={`font-poppins font-medium text-xs md:text-sm leading-none ${
                    isToggled ? 'text-[#049523]' : 'text-[#FB4C2F]'
                  }`}
                >
                  {isToggled ? 'Active' : 'Inactive'}
                </span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isToggled}
                  onChange={handleToggleChange}
                  className="sr-only peer"
                />
                <div
                  className={`w-9 h-[18px] p-[2px] rounded-[120px] relative peer-focus:outline-none transition-all ${
                    isToggled ? 'bg-[#0102CE]' : 'bg-[#CCCCCC]'
                  }`}
                >
                  {/* Circle with icon */}
                  <div
                    className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white flex items-center justify-center transition-all text-[8px] font-bold ${
                      isToggled
                        ? 'left-[calc(100%-16px)] text-[#0102CE]'
                        : 'left-[2px] text-[#CCCCCC]'
                    }`}
                  >
                    {isToggled ? '✓' : '−'}
                  </div>
                </div>
              </label>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionTierCard;
